import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from '../ChildrenContext.js';
import MultiValueView from './MultiValueView';

/**
 * Jira-like wrapper for any multi-value editor (multiselect lists, Offer, goals):
 * a collapsed one-line view (MultiValueView) <-> the editor (children) with apply / cancel buttons.
 *
 * The editor keeps passing its changes to the parent as usual, so the filter's Apply always sees them.
 * Cancel (or Esc) calls onRestore(snapshot) with the `value` the editor was opened with.
 * Click / tap outside closes the editor and keeps the values (ignored while `ignoreOutsideClick`,
 * e.g. while a modal of the editor is open).
 *
 * Free-text fields use the same behaviour built into Multiselects/Custom.js.
 * Styles: templates/<template>/styles/blocks/multi-value.less
 */
const MultiValueField = ({ titles = [], value, onRestore, ignoreOutsideClick = false, children, t }) => {
    const wrapperRef = useRef(null);
    const snapshotRef = useRef(null);
    const ignoreOutsideClickRef = useRef(ignoreOutsideClick);
    const [editing, setEditing] = useState(() => !titles.length);
    const [focusOnEdit, setFocusOnEdit] = useState(false);
    // apply/cancel buttons appear only after the user starts working with the field
    // (an empty field is shown as the editor right away, e.g. a just added filter - no buttons yet)
    const [active, setActive] = useState(false);
    const activate = useCallback(() => setActive(true), []);

    useEffect(() => {
        ignoreOutsideClickRef.current = ignoreOutsideClick;
    }, [ignoreOutsideClick]);

    const startEdit = useCallback(() => {
        snapshotRef.current = value;
        setFocusOnEdit(true);
        setActive(true);
        setEditing(true);
    }, [value]);

    const finishEdit = useCallback(() => {
        snapshotRef.current = null;
        setActive(false);
        setEditing(false);
    }, []);

    const cancelEdit = useCallback(() => {
        const snapshot = snapshotRef.current;
        snapshotRef.current = null;
        onRestore && onRestore(snapshot === undefined ? null : snapshot);
        setActive(false);
        setEditing(false);
    }, [onRestore]);

    useEffect(() => {
        if (!editing) {
            return undefined;
        }
        const onPointerDown = (event) => {
            if (ignoreOutsideClickRef.current) {
                return;
            }
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                finishEdit();
            }
        };
        document.addEventListener('mousedown', onPointerDown, true);
        document.addEventListener('touchstart', onPointerDown, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown, true);
            document.removeEventListener('touchstart', onPointerDown, true);
        };
    }, [editing, finishEdit]);

    useEffect(() => {
        if (editing && focusOnEdit && wrapperRef.current) {
            const input = wrapperRef.current.querySelector('input');
            if (input) {
                input.focus();
                // open the list right away (a click on the input opens the react-widgets popup)
                input.click();
            }
            setFocusOnEdit(false);
        }
    }, [editing, focusOnEdit]);

    const onKeyDown = useCallback((event) => {
        if (event.key !== 'Escape' && event.keyCode !== 27) {
            return;
        }
        // Esc first closes an open dropdown of the editor; only then it cancels the editing
        if (wrapperRef.current && wrapperRef.current.querySelector('[aria-expanded="true"]')) {
            return;
        }
        event.preventDefault();
        cancelEdit();
    }, [cancelEdit]);

    const staticNames = (t && t.reportPage) || {};
    const staticActions = (t && t.actions) || {};

    if (!editing) {
        return (
            <div className="multi-value">
                <MultiValueView
                    values={titles}
                    placeholder={staticNames.addValues}
                    editLabel={staticActions.edit || 'Edit'}
                    onEdit={startEdit}
                />
            </div>
        );
    }

    // keep the focus in the editor while pressing the apply/cancel buttons
    const keepFocus = (event) => event.preventDefault();

    return (
        <div className="multi-value multi-value--editing" ref={wrapperRef} onMouseDownCapture={activate} onTouchStartCapture={activate} onFocusCapture={activate} onKeyDown={onKeyDown}>
            {children}
            {!titles.length ? <span className="multi-value__search-icon" aria-hidden="true"><i className="fa fa-search" /></span> : null}
            {active ? (
            <div className="multi-value__actions">
                <button
                    type="button"
                    className="multi-value__action multi-value__action--apply"
                    title={staticActions.apply || 'Apply'}
                    aria-label={staticActions.apply || 'Apply'}
                    onMouseDown={keepFocus}
                    onClick={finishEdit}
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" stroke="currentColor" strokeWidth="1"/>
                    </svg>
                </button>
                <button
                    type="button"
                    className="multi-value__action multi-value__action--cancel"
                    title={staticActions.cancel || 'Cancel'}
                    aria-label={staticActions.cancel || 'Cancel'}
                    onMouseDown={keepFocus}
                    onClick={cancelEdit}
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" stroke="currentColor" strokeWidth="1"/>
                    </svg>
                </button>
            </div>
            ) : null}
        </div>
    );
};

MultiValueField.propTypes = {
    titles: PropTypes.array,
    value: PropTypes.any,
    onRestore: PropTypes.func,
    ignoreOutsideClick: PropTypes.bool,
    children: PropTypes.node,
    t: PropTypes.object
};

export default withTranslation(MultiValueField);
